import { Injectable, signal, computed, inject } from '@angular/core';
import { UserRoleDto } from '@app/core/models/user-role.model';
import { AuthStore } from '@app/core/state/auth.store';

/**
 * Interface for tenant context derived from UserRole validation
 * Simplified for single tenant per user only
 */
export interface TenantContext {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  userSub: string;
  roleName: string;
  roleId: string;
  isActive: boolean;
}

/**
 * Service to manage single tenant state using Angular 18 Signals
 * Stores the validated tenant information from UserRoleService response
 * No tenant switching - one user = one tenant
 */
@Injectable({
  providedIn: 'root'
})
export class TenantStateService {
  private readonly authStore = inject(AuthStore);

  // Private signal to hold the current tenant context
  private readonly _tenantContext = signal<TenantContext | null>(null);

  // Public readonly signals
  readonly tenantContext = this._tenantContext.asReadonly();
  
  // Computed signals for easy access to specific properties
  readonly tenantId = computed(() => this._tenantContext()?.tenantId ?? '');
  readonly tenantName = computed(() => this._tenantContext()?.tenantName ?? '');
  readonly tenantSlug = computed(() => this._tenantContext()?.tenantSlug ?? '');
  readonly isActive = computed(() => this._tenantContext()?.isActive ?? false);
  readonly hasValidTenant = computed(() => {
    const context = this._tenantContext();
    return context != null && context.isActive && context.tenantId.length > 0;
  });

  constructor() {
    // Try to restore tenant context from localStorage on service initialization
    this.restoreFromStorage();
  }

  /**
   * Sets the single tenant context from UserRoleDto (after successful login validation)
   * User is assigned to exactly one tenant - no switching allowed
   * @param userRole The validated user role data from API
   */
  setTenantFromUserRole(userRole: UserRoleDto): void {
    if (!userRole) {
      return;
    }

    const tenantContext: TenantContext = {
      tenantId: userRole.tenantId,
      tenantName: userRole.tenantName,
      tenantSlug: userRole.tenantSlug,
      userSub: userRole.userSub,
      roleName: userRole.roleName,
      roleId: userRole.roleId,
      isActive: userRole.isActive
    };

    this._tenantContext.set(tenantContext);
    this.saveToStorage(tenantContext);
  }

  /**
   * Clears the tenant context (typically on logout)
   */
  clearTenant(): void {
    this._tenantContext.set(null);
    this.clearStorage();
  }

  /**
   * Gets the current tenant ID for use in HTTP headers.
   * Uses tenant_id from JWT claims (AuthStore) as the primary source,
   * with TenantContext signal as a secondary check.
   * @returns The current tenant ID or empty string if not available
   */
  getCurrentTenantId(): string {
    const authState = this.authStore.userInfoData();
    if (!authState.accessToken || (authState.tokenExpiry && authState.tokenExpiry <= Date.now())) {
      return '';
    }

    const authenticatedSubject =
      this.authStore.getSubject()?.trim() || this.authStore.getUserId()?.trim() || '';
    if (!authenticatedSubject) {
      return '';
    }

    const clientIdFromAuth = this.authStore.getClientId()?.trim() || '';
    if (clientIdFromAuth) {
      return clientIdFromAuth.toLowerCase() === 'system' ? '' : clientIdFromAuth;
    }

    const context = this._tenantContext();
    const tenantId = context?.tenantId?.trim() || '';
    const contextSubject = context?.userSub?.trim() || '';
    if (
      context?.isActive &&
      tenantId &&
      tenantId.toLowerCase() !== 'system' &&
      contextSubject === authenticatedSubject
    ) {
      return tenantId;
    }

    return '';
  }

  /**
   * Checks if tenant context is valid and active
   * @returns True if tenant context is valid and active
   */
  isValidTenant(): boolean {
    return this.hasValidTenant();
  }

  /**
   * Saves tenant context to localStorage for persistence across sessions
   * @param context The tenant context to save
   */
  private saveToStorage(context: TenantContext): void {
    try {
      localStorage.setItem('tenant_context', JSON.stringify(context));
    } catch {
      // Local persistence is best effort.
    }
  }

  /**
   * Restores tenant context from localStorage
   */
  private restoreFromStorage(): void {
    try {
      const stored = localStorage.getItem('tenant_context');
      if (stored) {
        const context: TenantContext = JSON.parse(stored);
        if (context.tenantId && context.tenantName && context.userSub) {
          this._tenantContext.set(context);
        } else {
          this.clearStorage();
        }
      }
    } catch {
      this.clearStorage();
    }
  }

  /**
   * Clears tenant context from localStorage
   */
  private clearStorage(): void {
    try {
      localStorage.removeItem('tenant_context');
    } catch {
      // Local persistence is best effort.
    }
  }
}
